import React, { createContext, useContext, useState, ReactNode } from 'react';
import { OffertType, RoomData } from '../types';

type CategoriesOfferType = [string, OffertType];

type CategoriesOfferContextType = {
  estimates: CategoriesOfferType[];
  rotAvdrag: boolean;
  addData: (categoryKey: string, offer: OffertType) => void;
  removeData: (categoryKey: string) => void;
  toggleRotAvdrag: () => void;
  setRotAvdrag: (arg0: boolean) => void;
  getCost: () => { finalCost: number; rotAmount: number };
  orderId?: number;
  setOrderId: (arg0: number) => void;
  editMode: boolean;
  setEditMode: (arg0: boolean) => void;
  wpId?: number;
  setWpId: (arg0: number | undefined) => void;
  roomInfo?: RoomData;
  setRoomInfo: (arg0: RoomData) => void;
  questionErrors: number[];
  setQuestionErrors: (arg0: number[]) => void;
  loading: boolean;
  setLoading: (arg0: boolean) => void;
  finished: boolean;
  setFinished: (arg0: boolean) => void;
  startFee: number | undefined;
  setStartFee: (arg0: number) => void;
};

const CategoriesOfferContext = createContext<
  CategoriesOfferContextType | undefined
>(undefined);

const CategoriesOfferContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [estimates, setData] = useState<CategoriesOfferType[]>([]);
  const [rotAvdrag, setRotAvdrag] = useState(true);
  const [orderId, setOrderId] = useState<number | undefined>();
  const [wpId, setWpId] = useState<number>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [roomInfo, setRoomInfo] = useState<RoomData>();
  const [questionErrors, setQuestionErrors] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [finished, setFinished] = useState<boolean>(false);
  const [startFee, setStartFee] = useState<number>();

  const getCost = () => {
    const total = estimates.reduce(
      (accumulator, [, offer]) => accumulator + offer.price,
      0
    );
    const cost = rotAvdrag ? total * 0.7 : total;
    const rot = rotAvdrag ? total * 0.3 : 0;

    return { finalCost: cost, rotAmount: rot };
  };

  const addData = (categoryKey: string, offer: OffertType) => {
    setOrderId(offer.room_id);
    setData((prevData) => {
      const index = prevData.findIndex(([key, _]) => key === categoryKey);

      if (index !== -1) {
        return prevData.map(([key, existingOffer], i) =>
          i === index ? [key, offer] : [key, existingOffer]
        );
      } else {
        return [...prevData, [categoryKey, offer]];
      }
    });
  };

  const toggleRotAvdrag = () => {
    setRotAvdrag(!rotAvdrag);
  };

  const removeData = (categoryKey: string) => {
    setData((prevData) => prevData.filter(([key, _]) => key !== categoryKey));
  };

  const contextValue = {
    estimates,
    rotAvdrag,
    addData,
    removeData,
    toggleRotAvdrag,
    getCost,
    orderId,
    setOrderId,
    editMode,
    setEditMode,
    setRotAvdrag,
    wpId,
    setWpId,
    roomInfo,
    setRoomInfo,
    questionErrors,
    setQuestionErrors,
    loading,
    setLoading,
    finished,
    setFinished,
    startFee,
    setStartFee,
  };

  return (
    <CategoriesOfferContext.Provider value={contextValue}>
      {children}
    </CategoriesOfferContext.Provider>
  );
};

export const useCategoriesOfferContext = (): CategoriesOfferContextType => {
  const context = useContext(CategoriesOfferContext);
  if (!context) {
    throw new Error(
      'useCategoriesOfferContext must be used within a CategoriesOfferContextProvider'
    );
  }
  return context;
};

export default CategoriesOfferContextProvider;
